import React from "react";

import { Cell, Pie, PieChart } from "recharts";

import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import BorderBox from "@src/sharedComponents/BorderBox/BorderBox";
import BorderBoxBody from "@src/sharedComponents/BorderBox/BorderBoxBody";
import BorderBoxHeader from "@src/sharedComponents/BorderBox/BorderBoxHeader";
import TooltipBasic from "@src/sharedComponents/Tooltip/TooltipBasic";
import Text from "@src/sharedComponents/Typography/Text";

import styles from "./SuccessMeter.module.css";

const cx = 200;
const cy = 190;
const iR = 140;
const oR = 180;

interface Props {
  score: number; // should be out of 100 (0-100)
}

const SuccessMeter = ({ score = 0 }: Props): JSX.Element => {
  const {
    color: { $iconDanger, $iconWarning, $iconSuccess },
  } = useTheme();

  let validScore = score >= 100 ? 99 : score;
  if (validScore < 0) {
    validScore = 0;
  }

  const data = [
    { name: "A", value: validScore, color: "" }, // the color is set below upon render
    { name: "B", value: 1, color: "white" },
    { name: "C", value: 100 - (validScore + 1), color: "gray" },
  ];

  return (
    <BorderBox className={styles.container}>
      <BorderBoxHeader
        className={styles.header}
        start={
          <Text variant="body" color={"text"}>
            Projected Plan Success
          </Text>
        }
        end={
          <div className={styles.flexRow}>
            <Text variant="body" color={"textSubtle"}>
              What is chance of success?
            </Text>

            <TooltipBasic
              type="secondary"
              text="The chance of success represents the probability of reaching the end of your plan with a net worth greater than $0, based on the assumptions in the plan."
            />
          </div>
        }
      />

      <BorderBoxBody className={styles.body}>
        <div className={styles.chart}>
          <PieChart width={400} height={210}>
            <defs>
              <linearGradient id="lgColor">
                <stop offset="10%" stopColor={$iconDanger} />
                <stop offset="50%" stopColor={$iconWarning} />
                <stop offset="90%" stopColor={$iconSuccess} />
              </linearGradient>
            </defs>

            <Pie
              dataKey="value"
              startAngle={180}
              endAngle={0}
              cornerRadius={2}
              data={[data[0]]}
              cx={cx}
              cy={cy}
              innerRadius={iR}
              outerRadius={oR}
              fill="none"
              stroke="none"
            >
              <Cell fill={"url(#lgColor)"} />
            </Pie>

            <Pie
              dataKey="value"
              startAngle={180}
              endAngle={0}
              cornerRadius={2}
              data={data}
              cx={cx}
              cy={cy}
              innerRadius={iR}
              outerRadius={oR}
              fill="none"
              stroke="none"
            >
              {data.map((el, idx) => (
                <Cell key={idx} fill={idx === 0 ? "none" : el.color} />
              ))}
            </Pie>
          </PieChart>
        </div>

        <div className={styles.label}>
          <div className={styles.score}>
            <div className={styles.scoreText}>{score}</div>

            <div className={styles.percentChar}>%</div>
          </div>

          <Text variant="heading1" color={"text"}>
            Chance of Success
          </Text>
        </div>
      </BorderBoxBody>
    </BorderBox>
  );
};

export default SuccessMeter;
